import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Crown, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await axios.post(`${API}/auth/forgot-password`, { email }); setSent(true); } catch {} finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#000e2a] flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-8">
          <Crown className="w-10 h-10 text-[#fbdd68] mx-auto mb-2" />
          <p className="text-[#8892b0] text-sm font-mono uppercase tracking-widest">BIG Hat Command Center</p>
        </div>
        <Card className="glass-card rounded-2xl border-0 bg-transparent">
          <CardHeader>
            <CardTitle className="text-white text-xl">Reset Password</CardTitle>
            <CardDescription className="text-[#8892b0]">{sent ? 'Check your email' : "We'll send you a reset link"}</CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-[#22c55e] mx-auto mb-3" />
                <p className="text-white mb-2">Email sent!</p>
                <p className="text-sm text-[#8892b0]">If an account exists, you'll receive a reset link.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[#8892b0]">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8892b0]" />
                    <Input data-testid="forgot-password-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com" className="pl-10 bg-[#000e2a]/80 text-white placeholder-[#8892b0] border-[rgba(251,221,104,0.2)] focus:border-[#fbdd68]" required />
                  </div>
                </div>
                <Button data-testid="forgot-password-submit-button" type="submit" disabled={loading}
                  className="w-full bg-[#fbdd68] text-[#000e2a] font-bold uppercase tracking-wider hover:bg-[#ffe685]">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            )}
            <div className="mt-4 text-center">
              <Link data-testid="back-to-login-link" to="/login" className="text-sm text-[#fbdd68] hover:text-[#ffe685] inline-flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const token = new URLSearchParams(window.location.search).get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 10) { setError('Password must be at least 10 characters'); return; }
    setLoading(true);
    try { await axios.post(`${API}/auth/reset-password`, { token, new_password: password }); setSuccess(true); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to reset password'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#000e2a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="glass-card rounded-2xl border-0 bg-transparent">
          <CardHeader><CardTitle className="text-white text-xl">Set New Password</CardTitle></CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-[#22c55e] mx-auto mb-3" />
                <p className="text-white mb-2">Password Reset!</p>
                <Link data-testid="go-to-login-link" to="/login" className="text-[#fbdd68] hover:text-[#ffe685] font-semibold">Sign In</Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && <p className="text-[#ef4444] text-sm">{error}</p>}
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[#8892b0]">New Password</Label>
                  <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="bg-[#000e2a]/80 text-white border-[rgba(251,221,104,0.2)]" required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[#8892b0]">Confirm Password</Label>
                  <Input data-testid="reset-password-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="bg-[#000e2a]/80 text-white border-[rgba(251,221,104,0.2)]" required />
                </div>
                <Button data-testid="reset-password-submit-button" type="submit" disabled={loading}
                  className="w-full bg-[#fbdd68] text-[#000e2a] font-bold uppercase tracking-wider hover:bg-[#ffe685]">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
